import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { getSsoSetup, postSsoSetup } from '@/api/settings';
import { handleKnownSettingsApiErrors } from '@/stores/settings/errorHandling';
import { useAppLoading } from '@/stores/app/loading';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useSettingsSsoSetupForm } from '@/stores/settings/ssoSetupForm';

export const useSettingsSsoSetup = defineStore('settingsSsoSetup', () => {
  // other stores
  const form = useSettingsSsoSetupForm();
  const { setLoading } = useAppLoading();
  const { addSuccessSnackbar, addErrorSnackbar } = useAppSnackbar();

  // state
  const isActiveRef = ref(false);
  const isForcedRef = ref(false);
  const isGoogleRef = ref(false);
  const isMicrosoftRef = ref(false);
  const nameRef = ref('');
  const domainsRef = ref([]);
  const lastSsoLoginDateRef = ref(null);
  const entityIdRef = ref('');
  const samlUrlRef = ref('');
  const certificateRef = ref('');

  const setupIsReady = computed(() => entityIdRef.value
    && samlUrlRef.value
    && certificateRef.value
    && certificateRef.value !== 'placeholder');

  // check if the error is validation error and update form accordingly
  const handleValidationError = (error) => {
    const { response } = error;

    if (response && response.code === 'validation_failed') {
      form.setFormValues(response.attributes);
      form.setBackendErrors(response.errors);
      return true;
    }

    return false;
  };

  // check if the error is known or generic and show proper alert
  const handleError = (error) => {
    const isKnownError = handleKnownSettingsApiErrors({ context: 'SSO Setup settings', error });

    if (!isKnownError) {
      addErrorSnackbar({ text: `Account settings - ${error.message}` });
      throw error;
    }
  };

  const load = async () => {
    try {
      setLoading({ pageLoading: true });

      const {
        isActive, isForced, name, entityId, certificate, samlUrl, domains, lastSsoLoginDate,
        isGoogle, isMicrosoft,
      } = await getSsoSetup();

      form.setFormValues({ entityId, samlUrl, certificate });
      isActiveRef.value = isActive;
      isForcedRef.value = isForced;
      isGoogleRef.value = isGoogle;
      isMicrosoftRef.value = isMicrosoft;
      nameRef.value = name;
      entityIdRef.value = entityId;
      certificateRef.value = certificate;
      samlUrlRef.value = samlUrl;
      domainsRef.value = domains;
      lastSsoLoginDateRef.value = lastSsoLoginDate;
    } catch (error) {
      handleError(error);
    } finally {
      setLoading({ pageLoading: false });
    }
  };

  const update = async ({
    isActive, isForced, entityId, certificate, samlUrl, isGoogle, isMicrosoft,
  }) => {
    try {
      setLoading({ pageLoading: true });

      const { success } = await postSsoSetup({
        isActive, isForced, entityId, certificate, samlUrl, isGoogle, isMicrosoft,
      });
      if (success) {
        if (!isActive) {
          addSuccessSnackbar({
            text: 'Your SSO configuration has been deleted',
          });
        }
        await load();
      }
    } catch (error) {
      if (!handleValidationError(error)) {
        handleError(error);
      }
    } finally {
      setLoading({ pageLoading: false });
    }
  };

  return {
    name: nameRef,
    isActive: isActiveRef,
    isForced: isForcedRef,
    isGoogle: isGoogleRef,
    isMicrosoft: isMicrosoftRef,
    lastSsoLoginDate: lastSsoLoginDateRef,
    domains: domainsRef,
    setupIsReady,
    load,
    update,
  };
});
